import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { SectionReveal } from '@/components/brand/SectionReveal';
import { ServiceIcon } from '@/components/brand/ServiceIcon';
import { SERVICE_CATEGORIES } from '@/lib/utils';
import {
    CARD_INITIAL,
    CARD_WHILE_IN_VIEW,
    CARD_VIEWPORT,
    buildCardTransition,
} from '@/lib/motion';

// Subtle image filter that keeps the photograph in the brand palette
// (desaturated + slightly darker) so colour from the card image never fights
// with the gold accents or the dark canvas behind it.
const CARD_IMAGE_STYLE = {
    filter: 'saturate(0.78) brightness(0.78) contrast(1.05)',
};

const ServiceCard = ({ service, delay }) => {
    const transition = useMemo(() => buildCardTransition(delay), [delay]);
    return (
        <motion.div
            initial={CARD_INITIAL}
            whileInView={CARD_WHILE_IN_VIEW}
            viewport={CARD_VIEWPORT}
            transition={transition}
            className="h-full"
        >
            <Link
                to={`/services/${service.slug}`}
                aria-label={service.name}
                data-testid="service-card"
                className="group relative flex flex-col h-full overflow-hidden rounded-la border border-white/10 bg-la-canvas hover:border-la-gold/45 transition-[border-color,box-shadow,transform] duration-500 hover:shadow-[0_24px_60px_-30px_rgba(201,168,106,0.45)]"
            >
                {/* Image banner */}
                <div className="relative aspect-[16/10] overflow-hidden bg-la-canvas2">
                    <img
                        src={service.image}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                        style={CARD_IMAGE_STYLE}
                    />
                    {/* Dark gradient overlay — stronger at the bottom so the
                        category badge sits cleanly on top of any imagery */}
                    <div
                        aria-hidden
                        className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-[0.78]"
                        style={{
                            background:
                                'linear-gradient(180deg, rgba(6,8,12,0.20) 0%, rgba(6,8,12,0.55) 60%, rgba(6,8,12,0.92) 100%)',
                        }}
                    />
                    {/* Icon badge — glassmorphism + gold border */}
                    <span
                        aria-hidden
                        className="absolute top-5 left-5 inline-flex items-center justify-center h-11 w-11 rounded-la-sm border border-la-gold/35 bg-la-canvas/55 backdrop-blur-md text-la-gold transition-shadow duration-300 group-hover:shadow-[0_0_24px_-6px_rgba(201,168,106,0.65)]"
                    >
                        <ServiceIcon name={service.icon} className="h-[18px] w-[18px]" />
                    </span>
                </div>

                {/* Body */}
                <div className="flex flex-col flex-1 p-7 sm:p-8">
                    <h3 className="font-display font-normal text-[22px] sm:text-[24px] text-la-text leading-tight tracking-[-0.005em]">
                        {service.name}
                    </h3>
                    <p className="mt-3 text-[13.5px] text-la-text2 leading-relaxed flex-1">
                        {service.summary}
                    </p>
                    <div className="mt-7 pt-5 border-t border-white/8 flex items-center justify-between">
                        <span className="text-[11.5px] tracking-[0.16em] uppercase text-la-text2 group-hover:text-la-text transition-colors duration-300">Learn more</span>
                        <ArrowUpRight className="h-4 w-4 text-la-text3 group-hover:text-la-gold transition-[color,transform] duration-300 group-hover:translate-x-0.5" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
};

const ServicesHeader = () => (
    <SectionReveal>
        <div className="flex items-end justify-between flex-wrap gap-6">
            <div className="max-w-2xl">
                <div className="meta-eyebrow">Our Services</div>
                <h2 className="mt-5 font-display font-normal text-[34px] sm:text-[40px] lg:text-[46px] leading-[1.1] tracking-[-0.005em] text-la-text text-balance">
                    Practical consultancy across six core areas.
                </h2>
            </div>
            <Link
                to="/contact"
                className="text-[12.5px] tracking-[0.10em] uppercase text-la-text2 hover:text-la-text inline-flex items-center gap-1.5 underline-grow"
            >
                Request a brief <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
        </div>
    </SectionReveal>
);

export const ServicesGrid = () => (
    <section
        id="services"
        className="relative py-24 sm:py-28 lg:py-32 border-t border-white/8 bg-la-canvas"
    >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
            <ServicesHeader />
            <div
                data-testid="services-grid"
                className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7"
            >
                {SERVICE_CATEGORIES.map((service, idx) => (
                    <ServiceCard
                        key={service.slug}
                        service={service}
                        delay={(idx % 6) * 0.04}
                    />
                ))}
            </div>
        </div>
    </section>
);

export default ServicesGrid;
